/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import { getPageViewModifier, getPageZoomLevel } from "selectors/zoom";
import { pageViewModifiers } from "constants/enums";
import { isNumber } from "utils/lodashUtils";
import { getPageBeforeOrAtOffset } from "selectors/pageNumber";
import { getPagesViewXPos } from "selectors/pagedViewState";
import { noPreviewErrorCodes } from "constants/enums";
import { isSinglePageMode, isFullScreenMode } from "selectors/documentSelectors";
import { pagesInView } from "selectors/pagedViewSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getTopPanelHeight, getTopPanelVisibility } from "../selectors/topPanelSelectors";

class DocumentViewStore {
  @observable width;
  @observable height;
  @observable documentViewHandle;
  @observable noPreviewErrorCode;
  @observable lastPageExtraBottomSpace;
  @observable documentVisibleHeight;
  @observable showFallbackPrintLoader;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init() {
    this.width = 0;
    this.height = 0;
    this.documentViewHandle = null;
    this.noPreviewErrorCode = noPreviewErrorCodes.NO_ERROR;
    this.lastPageExtraBottomSpace = 0;
    this.documentVisibleHeight = 0;
    this.showFallbackPrintLoader = false;
  }

  @action
  setLastPageExtraBottomSpace(value) {
    this.lastPageExtraBottomSpace = value;
  }

  @action
  setFallbackPrintLoaderStatus(value) {
    this.showFallbackPrintLoader = value;
  }

  @action
  setNoPreviewErrorCode(errorCode) {
    if (isNumber(errorCode)) {
      this.noPreviewErrorCode = errorCode;
    }
  }

  _setViewPortDimensionsForSinglePageMode() {
    const currentPageIndex = getCurrentPageIndex(this.stores);
    const fitPageZoomLevel = this.stores.zoom.getFitPageZoomLevel(currentPageIndex);
    this.stores.zoom.setPageZoomLevel(fitPageZoomLevel);
    const pagesInViewObj = pagesInView(this.stores);
    this.stores.documentStore.reloadPages(pagesInViewObj);
  }

  @action
  setViewPortDimensions(width, height) {
    // get current view state.
    const topOffset = getTopPanelVisibility(this.stores) ? getTopPanelHeight() : 0;
    let { pageIndex, pageBottomMargin, yInPage } = getPageBeforeOrAtOffset(this.stores, topOffset);
    if (yInPage !== -1) {
      yInPage /= getPageZoomLevel(this.stores, pageIndex);
    }
    let xInPage = getPagesViewXPos(this.stores) / getPageZoomLevel(this.stores, pageIndex);

    if (isNumber(width) && width >= 0) {
      this.width = width;
    }

    if (isNumber(height) && height >= 0) {
      this.height = height;
    }

    const newPageViewModifier = getPageViewModifier(this.stores);
    if (isFullScreenMode(this.stores)) {
      this._setViewPortDimensionsForSinglePageMode();
    } else if (newPageViewModifier !== pageViewModifiers.NO_MOD) {
      this.stores.zoom.setPageViewModifier({ newPageViewModifier }, { pageIndex, xInPage, yInPage, pageBottomMargin });
    } else if (isSinglePageMode(this.stores)) {
      this._setViewPortDimensionsForSinglePageMode();
    }
    this.stores.zoom.handleMobileResizing();
  }

  @action
  setDocumentViewHandle(handle = null) {
    if (handle) {
      this.documentViewHandle = handle;
    }
  }

  @action
  setDocumentVisibleHeight(value) {
    this.documentVisibleHeight = value;
  }
}

export { DocumentViewStore };
